import { Card } from '@newcore/ui/card';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import type { McpServerClientType } from '@/api/user-portal-api-generated';
import {
  CLIENT_TYPE_DESCRIPTIONS,
  CLIENT_TYPE_LABELS,
} from '../constants/mcp-tester-content';
import { DocLinks } from './doc-links';

const NOT_PUBLISHED = 'Not published';

interface CapabilityCardProps {
  clientType: McpServerClientType;
  displayName: string;
  scopes: string[];
  documentationUrl: string;
  redirectUri: string;
}

function Entry({
  term,
  children,
}: {
  term: string;
  children: React.ReactNode;
}) {
  return (
    <div className='flex flex-col gap-0.5'>
      <Text as='dt' size={TextSizes.XS} className='text-tertiary uppercase'>
        {term}
      </Text>
      <dd className='flex flex-col gap-0.5'>{children}</dd>
    </div>
  );
}

export function CapabilityCard({
  clientType,
  displayName,
  scopes,
  documentationUrl,
  redirectUri,
}: CapabilityCardProps) {
  const docs = documentationUrl
    ? [{ label: "This server's own documentation", url: documentationUrl }]
    : [];

  return (
    <Card className='p-4'>
      <Text as='p' weight={FontWeights.SEMIBOLD} className='text-primary'>
        What this server supports
      </Text>
      <dl className='grid grid-cols-1 gap-4 pt-3 sm:grid-cols-2'>
        <Entry term='How we get a client'>
          <Text as='p' className='text-primary'>
            {CLIENT_TYPE_LABELS[clientType]}
          </Text>
          <Text as='p' className='text-tertiary'>
            {CLIENT_TYPE_DESCRIPTIONS[clientType]}
          </Text>
        </Entry>
        <Entry term='Published name'>
          <Text as='p' className='text-primary'>
            {displayName || NOT_PUBLISHED}
          </Text>
        </Entry>
        <Entry term='Scopes requested'>
          <Text as='p' className='text-primary break-words'>
            {scopes.length > 0 ? scopes.join(', ') : NOT_PUBLISHED}
          </Text>
        </Entry>
        {redirectUri && (
          <Entry term='Redirect URI to allow'>
            <Text
              as='p'
              size={TextSizes.XS}
              className='text-primary break-all font-mono'
            >
              {redirectUri}
            </Text>
          </Entry>
        )}
      </dl>
      {docs.length > 0 && <DocLinks links={docs} />}
    </Card>
  );
}
